import {
  Button,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionType,
} from 'platform/components';
import {HStack, Right, VStack} from 'platform/foundation';

import {pipe, reject} from 'ramda';

import {getOptionsFromServiceCaseOrderVariants} from '@dms/api/utils';
import {
  useGetServiceCaseOrderVariantsQuery,
  usePostServiceOrderChangeVariantMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {
  ChangeOrderVariantFormSchema,
  ChangeOrderVariantFormType,
} from '../types/ChangeOrderVariantFormType';

interface ChangeOrderVariantFormProps extends TestIdProps {
  serviceCaseId: string;
  orderId: string;
  orderVariant: string | Nullish;
  onClose: () => void;
  onSubmitted: () => void;
}

export function ChangeOrderVariantForm(props: ChangeOrderVariantFormProps) {
  const [postServiceOrderChangeVariant, {isLoading}] = usePostServiceOrderChangeVariantMutation();

  const {data: orderVariants} = useGetServiceCaseOrderVariantsQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const handleSubmit: FormSubmitHandler<ChangeOrderVariantFormType> = ({orderVariant}) =>
    postServiceOrderChangeVariant({
      orderVariantId: orderVariant,
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
    })
      .unwrap()
      .then(() => {
        props.onSubmitted();
        props.onClose();
      })
      .catch(handleApiError);

  const orderVariantOptions = pipe(
    getOptionsFromServiceCaseOrderVariants,
    reject<OptionType>((variant) => variant.value === props.orderVariant)
  )(orderVariants);

  return (
    <Form<ChangeOrderVariantFormType>
      onSubmit={handleSubmit}
      experimentalZodSchema={ChangeOrderVariantFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="orderVariant"
            type="choice"
            options={orderVariantOptions}
            label={i18n.t('entity.order.labels.orderVariant')}
            placeholder={i18n.t('general.labels.select')}
            menuInPortal
            data-testid={suffixTestId('orderVariant', props)}
          />
          <Right>
            <HStack spacing={2}>
              <Button
                onClick={props.onClose}
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                data-testid={suffixTestId('cancel', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.change')}
                isLoading={isLoading}
                data-testid={suffixTestId('change', props)}
              />
            </HStack>
          </Right>
        </VStack>
      )}
    </Form>
  );
}
